import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiSystem } from '../../state/types';
import { useSession } from '../../context/SessionContext';
import { getAuthHeaders } from '../../api/useSystemsApi';
import { useAssetApi } from '../../api/useAssetApi';

const steps = ['Asset Information', 'Upload Images', 'Confirm & Save'];
export type ImageTypeKey = 'exterior' | 'manual' | 'pano' | 'uc';
export const UPLOAD_TYPES: ImageTypeKey[] = ['exterior', 'manual', 'pano', 'uc'];

export const useAddAssetState = (onClose: () => void) => {
  const [activeStep, setActiveStep] = useState(0);
  const [assetId, setAssetId] = useState('');
  const [selectedConfigName, setSelectedConfigName] = useState('');
  const [assetIdError, setAssetIdError] = useState('');
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [isUploading, setIsUploading] = useState(false);
  const [assetPk, setAssetPk] = useState<string | null>(null);
  const [currentImageType, setCurrentImageType] = useState<ImageTypeKey>('exterior');
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [filesByType, setFilesByType] = useState<Record<ImageTypeKey, File[]>>({
    exterior: [],
    manual: [],
    pano: [],
    uc: [],
  });

  const [imageTags, setImageTags] = useState<Record<ImageTypeKey, Record<string, string>>>({
    exterior: {},
    manual: {},
    pano: {},
    uc: {},
  });

  const filesToUpload = useMemo(() => Object.values(filesByType).flat(), [filesByType]);

  const { selectedSystem, isLoading: systemsLoading } = useSession();
  const { createAssetApi, finalizeAssetApi, runAssetApi, uploadToS3Api } = useAssetApi();

  const selectedConfiguration = useMemo(() => {
    return selectedSystem?.configurations?.[selectedConfigName] ?? null;
  }, [selectedSystem, selectedConfigName]);

  const vinRegexPattern = useMemo(() => {
    return selectedConfiguration?.clientProcess?.vin_regex;
  }, [selectedConfiguration]);

  const vinLength = useMemo(() => {
    return selectedConfiguration?.clientProcess?.vin_length;
  }, [selectedConfiguration]);

  // Mirror the unit's validate_vin (bw-unit-local/server/app/mqtt.py): if the config
  // has a vin_regex use it, otherwise fall back to an exact vin_length check. This is
  // why bad VINs used to reach the unit — the frontend only checked the regex, but
  // configs like `umstest` have no regex, just vin_length: "17". Returns '' when valid.
  const computeVinError = useCallback((vin: string): string => {
    if (!vin) return '';
    if (vinRegexPattern) {
      try {
        return new RegExp(vinRegexPattern).test(vin)
          ? ''
          : `Make sure the asset ID matches ${vinRegexPattern}`;
      } catch {
        // malformed regex in config — fall back to length, like the unit does
      }
    }
    if (vinLength) {
      const n = parseInt(String(vinLength), 10);
      if (!Number.isNaN(n) && vin.length !== n) {
        return `Asset ID must be exactly ${n} characters (currently ${vin.length})`;
      }
    }
    return '';
  }, [vinRegexPattern, vinLength]);

  const configOptions = useMemo(() => {
    if (!selectedSystem?.configurations) return [];

    return Object.entries(selectedSystem.configurations).map(([configKey, config]: [string, any]) => ({
      value: configKey,
      label: config?.clientProcess?.name || configKey,
    }));
  }, [selectedSystem]);

  const noConfigsFound = !!(selectedSystem && configOptions.length === 0);

  const setImageTag = (fileName: string, tag: string, type: ImageTypeKey) => {
    setImageTags(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [fileName]: tag,
      }
    }));
  };

  useEffect(() => {
    if (selectedSystem && selectedSystem.configurations) {
      const defaultConfigName = Object.keys(selectedSystem.configurations).find(configName =>
        selectedSystem.configurations?.[configName]?.clientProcess?.is_default === true
      );

      if (defaultConfigName) {
        setSelectedConfigName(defaultConfigName);
      }
    }
  }, [selectedSystem]);

  useEffect(() => {
    setAssetIdError(computeVinError(assetId));
  }, [assetId, computeVinError]);

  const allUploadsComplete = useMemo(() => {
    if (filesToUpload.length === 0) return false;
    return filesToUpload.every(file => uploadProgress[file.name] === 100);
  }, [filesToUpload, uploadProgress]);

  const addFiles = (newFiles: File[], type: ImageTypeKey) => {
    setFilesByType(prev => {
      const existingFileNames = new Set(prev[type].map(f => f.name));
      const uniqueNewFiles = newFiles.filter(f => !existingFileNames.has(f.name));
      return {
        ...prev,
        [type]: [...prev[type], ...uniqueNewFiles]
      };
    });
  };

  const removeFile = (fileNameToRemove: string, type: ImageTypeKey) => {
    setFilesByType(prev => ({
      ...prev,
      [type]: prev[type].filter(file => file.name !== fileNameToRemove)
    }));

    setUploadProgress(prevProgress => {
      const newProgress = { ...prevProgress };
      delete newProgress[fileNameToRemove];
      return newProgress;
    });

    setImageTags(prev => {
      const newTagsForType = { ...prev[type] };
      delete newTagsForType[fileNameToRemove];
      return {
        ...prev,
        [type]: newTagsForType,
      };
    });
  };

  const uploadFilesToS3 = async (files: File[], presignedUrls: { [key: string]: string }) => {
    const uploadPromises = files.map(file => {
      const url = presignedUrls[file.name];

      if (!url) {
        console.error(`No presigned URL found for file: ${file.name}`);
        setUploadProgress(prev => ({ ...prev, [file.name]: -1 }));
        return Promise.resolve();
      }

      return uploadToS3Api(
        url,
        file,
        (percent) => {
          setUploadProgress(prev => ({ ...prev, [file.name]: percent }));
        });
    });

    await Promise.all(uploadPromises);
    console.log("All uploads finished.");
  };

  const finalizeAsset = async () => {
    if (!assetPk) {
      alert("Cannot finalize: Asset Primary Key is missing.");
      return;
    }

    setIsFinalizing(true);
    try {
      const payload = {
        pk: assetPk,
        imageExterior: filesByType.exterior.map(f => ({
          imageTag: imageTags.exterior[f.name] || 'Unassigned',
          filename: f.name
        })),
        imageManual: filesByType.manual.map(f => ({
          imageTag: imageTags.manual[f.name] || 'N/A',
          filename: f.name
        })),
        imagePano: filesByType.pano.map(f => ({
          imageTag: imageTags.pano[f.name] || 'N/A',
          filename: f.name
        })),
        imageUndercarriage: filesByType.uc.map(f => ({
          imageTag: imageTags.uc[f.name] || 'N/A',
          filename: f.name
        })),
      };

      console.log("--- FINALIZING ASSET ---");
      console.log("Payload for finalize-asset lambda:", JSON.stringify(payload, null, 2));

      await finalizeAssetApi(payload);

      alert("Asset successfully created!");
      onClose();
    } catch (error) {
      console.error("Failed to finalize asset:", error);
      alert("An error occurred during finalization. Please check the console.");
    } finally {
      setIsFinalizing(false);
    }
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      if (vinRegexPattern) {
        const regex = new RegExp(vinRegexPattern);
        if (!regex.test(assetId)) {
          const continueAnyway = window.confirm(`This Asset ID does not match the required format (${vinRegexPattern}).\n\nContinue anyway?`);
          if (!continueAnyway) return;
        }
      }
      setActiveStep((prev) => prev + 1);
      return;
    }

    if (activeStep === 1) {
      if (filesToUpload.length === 0) {
        alert("Please select at least one file to upload before continuing.");
        return;
      }

      setIsUploading(true);
      setActiveStep((prev) => prev + 1);

      try {
        const payloadFiles = Object.entries(filesByType).flatMap(([type, files]) =>
          files.map(file => ({
            filename: file.name,
            contentType: file.type,
            imageType: `${type}_raw`
          }))
        );

        const payload = {
          assetIdentifier: assetId,
          systemCode: selectedSystem?.systemCode,
          rooftopName: selectedConfigName,
          files: payloadFiles
        };

        await getAuthHeaders();
        const { pk, presignedUrls } = await createAssetApi(payload);

        setAssetPk(pk);
        await uploadFilesToS3(filesToUpload, presignedUrls);
      } catch (error) {
        console.error("Failed to create asset or upload files", error);
        alert("An error occurred. Please check the console and try again.");
      } finally {
        setIsUploading(false);
      }
      return;
    }

    if (activeStep === steps.length - 1) {
      console.log(`--- FINALIZING ASSET ${assetPk} ---`);
      await finalizeAsset();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (isUploading) return;
    setActiveStep((prev) => prev - 1);
  };

  const handleRunAsset = async () => {
    const rooftopId = selectedConfiguration?.clientProcess?.id;

    if (!assetId || !selectedSystem?.systemCode || !rooftopId) {
      alert("Asset ID, a selected system, and a configuration with a rooftop ID are required to run this action.");
      return;
    }

    // Validate the VIN against the selected config's rules (regex/length) BEFORE
    // firing the run — same rules the unit enforces — so we never send a run the
    // odroid will just reject.
    const vinError = computeVinError(assetId);
    if (vinError) {
      setAssetIdError(vinError);
      alert(`Cannot run this asset: ${vinError}`);
      return;
    }

    try {
      const payload = {
        assetId,
        rooftop_id: rooftopId,
        system_code: selectedSystem.systemCode
      };

      console.log("Triggering Run Asset with payload:", payload);
      await runAssetApi(payload);

      alert("Asset successfully triggered on the Odroid!");
      onClose();
    } catch (err) {
      console.error("Error running asset", err);
      alert("Failed to run asset. See the console for more details.");
    }
  };

  return {
    activeStep,
    handleNext,
    handleBack,
    handleRunAsset,
    assetId,
    setAssetId,
    assetIdError,
    systemsLoading,
    configOptions,
    selectedConfigName,
    setSelectedConfigName,
    noConfigsFound,
    selectedSystem: selectedSystem as ApiSystem | null,
    selectedConfiguration,
    steps,
    isUploading,
    filesByType,
    currentImageType,
    setCurrentImageType,
    addFiles,
    removeFile,
    allUploadsComplete,
    filesToUpload,
    isFinalizing,
    uploadProgress,
    imageTags,
    setImageTag
  };
};
